<script setup lang="ts">
import { lock, clearBodyLocks } from 'tua-body-scroll-lock'
import { defineAsyncComponent, onBeforeUnmount, onMounted, ref } from '#imports'

const YscIconsDelete = defineAsyncComponent(() => import('./YscIconsDelete.vue'))

interface Props {
  canClose?: boolean
  fullscreen?: boolean
  scrollLock?: boolean
  isHeaderLeftOnMobile?: boolean
  noPadding?: boolean
  isBorderOnHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canClose: true,
  fullscreen: false,
  scrollLock: true,
  isHeaderLeftOnMobile: false,
  noPadding: false,
  isBorderOnHeader: false,
})

const emit = defineEmits<{
  close: []
  mounted: []
}>()

const modalContent = ref()

const onClose = () => {
  if (!props.canClose) {
    return
  }

  emit('close')
}

const escForCloseHandler = ({ keyCode }: KeyboardEvent) => {
  if (keyCode === 27) {
    onClose()
  }
}

onMounted(() => {
  if (props.scrollLock) {
    lock(modalContent.value)
  }
  emit('mounted')
  window.addEventListener('keyup', escForCloseHandler)
})

onBeforeUnmount(() => {
  if (props.scrollLock) {
    clearBodyLocks()
  }
  window.removeEventListener('keyup', escForCloseHandler)
})
</script>

<template>
  <div
    class="fixed inset-0 z-40 m-0 flex h-full w-full flex-col justify-end bg-black bg-opacity-50 lg:justify-center"
    :class="{ 'cursor-pointer': canClose }"
    @click="onClose()"
  >
    <div
      ref="modalContent"
      class="no-scrollbar relative z-50 mx-auto w-full cursor-auto overflow-y-auto border-gray-200 bg-white lg:border"
      :class="fullscreen ? 'h-full flex flex-col' : 'max-h-[calc(100vh-56px)] max-w-screen-md rounded-t-md lg:max-h-[85vh] lg:rounded-md'"
      @click.stop
    >
      <slot name="header">
        <header
          :class="[
            isHeaderLeftOnMobile ? 'flex-row-reverse' : 'md:flex-row-reverse',
            { 'border-b border-gray-200': isBorderOnHeader },
            'sticky top-0 z-10 flex justify-between bg-white px-4 sm:px-10 py-4 sm:py-6',
          ]"
        >
          <button
            v-if="canClose"
            class="shrink-0 py-0.5 pr-0.5 md:pl-0.5 md:pr-0"
            @click="onClose()"
          >
            <YscIconsDelete class="h-6 w-6" />
          </button>

          <h2
            :class="[
              isHeaderLeftOnMobile ? 'text-left' : 'text-center',
              'grow font-semibold md:text-left text-2xl',
            ]"
          >
            <slot name="title" />
          </h2>
        </header>
      </slot>

      <div
        v-if="$slots.default"
        :class="{
          'my-4 px-4 pb-2 sm:my-6 sm:px-10 sm:pb-4': !noPadding,
          'flex-grow': fullscreen,
        }"
      >
        <slot />
      </div>

      <footer
        v-if="$slots.footer"
        class="sticky bottom-0 mt-12"
      >
        <div
          class="relative bg-white border-t border-gray-200 px-4 py-4 sm:px-10"
        >
          <slot name="footer" />
        </div>
      </footer>
    </div>
  </div>
</template>
